<template>
  <div class="kds">
    <div class="kds__topbar">
      <nav class="kds__tabs" role="tablist">
        <div class="kds__stations-scroll">
          <!-- One station per board (M971): the printer comes from the KDS
               link, or the last one used on this device — no switching. -->
          <span
            v-for="printer in visiblePrinters"
            :key="printer.id"
            class="kds__station kds__station--active"
          >
            <i class="material-icons-round kds__station-icon">restaurant</i>
            {{ printer.nickname || printer.name }}
          </span>
        </div>
      </nav>

      <!-- Right-side cluster mirrors the design's `flex items-center gap-4` —
           every control is included for layout completeness even if its
           handler is a placeholder (Print/Language/Settings have no wired
           action yet, per prior product calls). -->
      <div class="kds__controls ms-3">
        <!-- M1024: three beeps when a new order reaches this station. M1072:
             the branch's New order sound setting decides; this chip only
             shows it and opens Settings. -->
        <button
          type="button"
          class="kds__control kds__audio"
          :class="{ 'kds__audio--off': isSoundOff, 'kds__audio--pending': !settingsLoaded }"
          title="Change in Settings"
          @click="openSettings"
        >
          <i class="material-icons-round kds__control-icon">{{
            isSoundOff ? "volume_off" : "volume_up"
          }}</i>
          {{ soundLabel }}
        </button>

        <button
          type="button"
          class="kds__control kds__sort"
          :title="sortOrder === 'DESC' ? 'Showing newest first' : 'Showing oldest first'"
          :disabled="isUserFetching"
          @click="toggleSort"
        >
          <i class="material-icons-round kds__control-icon">sort</i>
          <span class="kds__sort-label">{{ sortLabel }}</span>
        </button>

        <button
          type="button"
          class="kds__control kds__icon-btn kds__lang"
          title="Language"
          @click="onLanguage"
        >{{ t.langLabel }}</button>

        <div class="kds__divider" aria-hidden="true"></div>

        <button
          type="button"
          class="kds__control kds__icon-btn"
          title="Print"
          @click="onPrint"
        >
          <i class="material-icons-round">print</i>
        </button>

        <button
          type="button"
          class="kds__control kds__icon-btn"
          title="Refresh"
          :disabled="isUserFetching"
          @click="refresh"
        >
          <i class="material-icons-round" :class="{ 'kds__icon--spinning': isUserFetching }">refresh</i>
        </button>

        <button
          type="button"
          class="kds__control kds__icon-btn"
          title="Settings"
          @click="openSettings"
        >
          <i class="material-icons-round">settings</i>
        </button>
      </div>
    </div>

    <nav class="kds__stations" role="tablist">
      <button
        type="button"
        class="kds__tab"
        :class="{ 'kds__tab--active': tab === ORDER_STATUS.PREPARING }"
        :disabled="isUserFetching"
        @click="changeTab(ORDER_STATUS.PREPARING)"
      >{{ t.preparing }}</button>
      <button
        type="button"
        class="kds__tab"
        :class="{ 'kds__tab--active': tab === ORDER_STATUS.READY }"
        :disabled="isUserFetching"
        @click="changeTab(ORDER_STATUS.READY)"
      >{{ t.ready }}</button>
    </nav>

    <div v-if="visibleChits.length" class="kds__grid">
      <OrderChit
        v-for="chit in visibleChits"
        :key="chit.order.transaction_id"
        :order="chit.order"
        :items="chit.items"
        :struck-keys="chit.struckKeys"
        :tab="tab"
        :now="now"
        :alert-after-ms="alertAfterMs"
        :alert-color="settings.alertColor"
        :ready-color="settings.readyColor"
        :name-field="settings.language"
        :show-description="settings.displayBothNameDesc"
        :show-open-time="settings.displayOpenTime"
        :description-field="settings.description"
        :is-selected="selectedTransactionId === chit.order.transaction_id"
        @done="onDone"
        @recall="onRecall"
        @collect="onCollect"
        @select="onSelect"
        @strike="onStrike"
      />
    </div>
    <div v-else-if="isFetchingList" class="kds__empty">
      <i class="material-icons-round kds__spinner">refresh</i>
    </div>
    <div v-else class="kds__empty">{{ t.noOrders }}</div>

    <!-- Settings modal — opened from the gear button on the topbar. -->
    <div
      class="modal fade"
      id="kds-settings-modal"
      tabindex="-1"
      aria-hidden="true"
      ref="settingsModal"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content kds-settings">
          <div class="modal-header kds-settings__header">
            <h5 class="modal-title">KDS Settings</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click="closeSettingsModal"
            ></button>
          </div>

          <div class="modal-body kds-settings__body">
            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Display unpaid bills</label>
              <label class="kds-toggle" :class="{ 'kds-toggle--on': draft.displayUnpaidBills }">
                <input type="checkbox" v-model="draft.displayUnpaidBills" />
                <span class="kds-toggle__track"><span class="kds-toggle__thumb"></span></span>
              </label>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Display open time</label>
              <label class="kds-toggle" :class="{ 'kds-toggle--on': draft.displayOpenTime }">
                <input type="checkbox" v-model="draft.displayOpenTime" />
                <span class="kds-toggle__track"><span class="kds-toggle__thumb"></span></span>
              </label>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <div>
                <label class="kds-settings__label">New order sound</label>
                <div class="kds-settings__hint">{{ newOrderSoundHint }}</div>
              </div>
              <div class="kds-segmented" role="radiogroup" aria-label="New order sound">
                <button
                  v-for="option in NEW_ORDER_SOUND_OPTIONS"
                  :key="option.value"
                  type="button"
                  role="radio"
                  class="kds-segmented__option"
                  :class="{ 'kds-segmented__option--active': draft.newOrderSound === option.value }"
                  :aria-checked="draft.newOrderSound === option.value"
                  @click="draft.newOrderSound = option.value"
                >
                  <i class="material-icons-round kds-segmented__icon">{{ option.icon }}</i>
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Default Alert time</label>
              <div class="kds-settings__inline-controls">
                <select
                  v-model.number="draft.alertMinutes"
                  class="kds-settings__alert-select"
                >
                  <option :value="5">5</option>
                  <option :value="10">10</option>
                  <option :value="15">15</option>
                </select>
                <span class="kds-settings__unit">minutes</span>
                <span class="kds-settings__unit kds-settings__unit--divider">Alert color</span>
                <input
                  type="color"
                  v-model="draft.alertColor"
                  class="kds-settings__color"
                  title="Alert color"
                />
              </div>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Ready color</label>
              <input
                type="color"
                v-model="draft.readyColor"
                class="kds-settings__color"
                title="Ready color"
              />
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Send WA notif when click Done</label>
              <label class="kds-toggle" :class="{ 'kds-toggle--on': draft.sendWaOnDone }">
                <input type="checkbox" v-model="draft.sendWaOnDone" />
                <span class="kds-toggle__track"><span class="kds-toggle__thumb"></span></span>
              </label>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Send WA notif when click Collect</label>
              <label class="kds-toggle" :class="{ 'kds-toggle--on': draft.sendWaOnCollect }">
                <input type="checkbox" v-model="draft.sendWaOnCollect" />
                <span class="kds-toggle__track"><span class="kds-toggle__thumb"></span></span>
              </label>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Language</label>
              <select v-model="draft.language" class="kds-settings__name-select">
                <option
                  v-for="option in NAME_FIELD_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                  :disabled="option.value === draft.description"
                >{{ option.label }}</option>
              </select>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Display both Name and Description</label>
              <label class="kds-toggle" :class="{ 'kds-toggle--on': draft.displayBothNameDesc }">
                <input type="checkbox" v-model="draft.displayBothNameDesc" />
                <span class="kds-toggle__track"><span class="kds-toggle__thumb"></span></span>
              </label>
            </div>

            <div class="kds-settings__row kds-settings__row--inline">
              <label class="kds-settings__label">Description</label>
              <select v-model="draft.description" class="kds-settings__name-select">
                <option
                  v-for="option in DESCRIPTION_FIELD_OPTIONS"
                  :key="option.value"
                  :value="option.value"
                  :disabled="option.value === draft.language"
                >{{ option.label }}</option>
              </select>
            </div>
          </div>

          <div class="modal-footer kds-settings__footer">
            <button
              type="button"
              class="btn btn-primary"
              @click="saveSettings"
            >SAVE</button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="closeSettingsModal"
            >CANCEL</button>
          </div>
        </div>
      </div>
    </div>

    <ModalServerError
      v-model:show="serverErrorVisible"
      :message="serverErrorMessage"
    />
  </div>
</template>

<script>
import "bootstrap/dist/js/bootstrap.bundle";
import Modal from "bootstrap/js/dist/modal";
import ModalServerError from "@/components/ModalServerError.vue";
import SettingStoreService from "../../../services/settingStore.service";
import MenusetService from "../../../services/product/menuset.service";
import TokenService from "../../../services/token.service";
import OrderChit from "./OrderChit.vue";
import { ORDER_STATUS } from "../mock_data";
import {
  resolvePrinterIds,
  isStationReady,
  toggleStationReady,
} from "../kds.utils";
import moment from "moment";
import BoardRealtime from "../../../services/board-realtime";

const POLL_INTERVAL_MS = 10000;

// With a live socket the timer is no longer how the board learns about orders —
// but it can never stop. The KDS endpoint runs behind stock.passcode in refresh
// mode, and that request is what slides the passcode's hour forward; a board
// that stopped calling it would lock mid shift. Five minutes keeps the hour well
// topped up and doubles as the net under a missed signal.
const HEARTBEAT_INTERVAL_MS = 300000;

// Signals arrive in bursts — one POS push moves several orders — and each would
// otherwise cost a full board read.
const COALESCE_MS = 400;

// connection.ready fires on every reconnect, so a websocket-server restart would
// have every board in every outlet re-read at the same instant.
// Twenty seconds, not five: an Apache or websocket restart drops every board in
// every outlet at once, and their catch-up reads land on the same 80 php-fpm
// workers as the customer screens reconnecting beside them. Only that catch-up
// read waits; a board.updated after the reconnect is read at once.
const RESYNC_JITTER_MS = 20000;
const TICK_INTERVAL_MS = 1000;
// M1024: the alert repeats on this beat until someone turns the audio off or,
// since M1071, Preparing is empty.
const ALERT_REPEAT_MS = 3000;
// M1072: per-branch choice of how the board sounds for a new order.
const NEW_ORDER_SOUND = { REPEAT: "repeat", ONCE: "once", OFF: "off" };
const NEW_ORDER_SOUND_OPTIONS = [
  {
    value: NEW_ORDER_SOUND.REPEAT,
    label: "Repeat",
    icon: "repeat",
    hint: "Beeps every 3 seconds until Preparing is empty",
  },
  {
    value: NEW_ORDER_SOUND.ONCE,
    label: "Once",
    icon: "notifications",
    hint: "Beeps once for each new order",
  },
  {
    value: NEW_ORDER_SOUND.OFF,
    label: "Off",
    icon: "volume_off",
    hint: "No sound on this board",
  },
];
const DEFAULT_ALERT_AFTER_MIN = 5;
const DEFAULT_ALERT_COLOR = "#ef4444";
// Chit colour on the READY tab. Ready orders are always past the alert
// threshold, so before M945 they all rendered in the alert colour and red
// stopped meaning "late". Default is the blue of the Ready tab / Done button.
const DEFAULT_READY_COLOR = "#3b82f6";

// KDS page-level labels. The page chrome is always English: the Language
// setting picks which of the item's four name columns the chit prints, it is
// not a UI locale (it used to be Main/Secondary, which also flipped this text
// to Chinese — M939).
const PAGE_LABELS = {
  alertAfter: "Alert after",
  minSuffix: "min",
  newest: "NEWEST",
  oldest: "OLDEST",
  preparing: "Preparing",
  ready: "Ready",
  noOrders: "No orders for this station.",
  langLabel: "EN",
};

// Which minty_item name column each radio writes. Values match what the BE
// stores in kds_language; "main"/"secondary" are the legacy values it maps.
const NAME_FIELD_OPTIONS = [
  { value: "name1", label: "Name 1" },
  { value: "name2", label: "Name 2" },
  { value: "name3", label: "Name 3" },
  { value: "name4", label: "Name 4" },
];
const LEGACY_NAME_FIELDS = { main: "name1", secondary: "name2" };

// Which column feeds the chit's second line while Display-both is on.
// "none" keeps the item's own Description, the way it worked before this
// picker existed (M942); a name may not repeat whatever Language uses.
const DESCRIPTION_NONE = "none";
const DESCRIPTION_FIELD_OPTIONS = [
  { value: DESCRIPTION_NONE, label: "None" },
  ...NAME_FIELD_OPTIONS,
];

function normaliseNameField(value) {
  if (NAME_FIELD_OPTIONS.some((o) => o.value === value)) return value;
  return LEGACY_NAME_FIELDS[value] ?? "name1";
}

function normaliseDescriptionField(value) {
  return DESCRIPTION_FIELD_OPTIONS.some((o) => o.value === value)
    ? value
    : DESCRIPTION_NONE;
}

function defaultSettings() {
  return {
    alertMinutes: DEFAULT_ALERT_AFTER_MIN,
    alertColor: DEFAULT_ALERT_COLOR,
    readyColor: DEFAULT_READY_COLOR,
    sendWaOnDone: false,
    sendWaOnCollect: false,
    language: "name1",
    displayBothNameDesc: false,
    description: DESCRIPTION_NONE,
    // Unpaid bills (minty_transactions.status = OPEN) reach the board unless
    // the merchant turns them off. Filtered in the listing query, not here.
    displayUnpaidBills: true,
    // OPEN (order created) time on the chit, next to PAID. Off unless the
    // merchant turns it on.
    displayOpenTime: false,
    newOrderSound: NEW_ORDER_SOUND.REPEAT,
  };
}

// BE returns settings in snake_case (matching the minty_mer_settings key
// names); the FE state uses camelCase. The two maps below convert between
// the wire format and the UI state.
function settingsFromApi(payload) {
  const base = defaultSettings();
  if (!payload || typeof payload !== "object") return base;
  return {
    alertMinutes: Number(payload.alert_minutes) || base.alertMinutes,
    alertColor: payload.alert_color || base.alertColor,
    readyColor: payload.ready_color || base.readyColor,
    sendWaOnDone: Boolean(Number(payload.send_wa_on_done)),
    sendWaOnCollect: Boolean(Number(payload.send_wa_on_collect)),
    language: normaliseNameField(payload.language ?? base.language),
    displayBothNameDesc: Boolean(Number(payload.display_both_name_description)),
    description: normaliseDescriptionField(payload.description ?? base.description),
    displayUnpaidBills: Boolean(Number(payload.display_unpaid_bills ?? 1)),
    displayOpenTime: Boolean(Number(payload.display_open_time ?? 0)),
    newOrderSound: NEW_ORDER_SOUND_OPTIONS.some((o) => o.value === payload.new_order_sound)
      ? payload.new_order_sound
      : base.newOrderSound,
  };
}

function settingsToApi(state) {
  return {
    alert_minutes: state.alertMinutes,
    alert_color: state.alertColor,
    ready_color: state.readyColor,
    send_wa_on_done: state.sendWaOnDone ? 1 : 0,
    send_wa_on_collect: state.sendWaOnCollect ? 1 : 0,
    language: state.language,
    display_both_name_description: state.displayBothNameDesc ? 1 : 0,
    description: state.description,
    display_unpaid_bills: state.displayUnpaidBills ? 1 : 0,
    display_open_time: state.displayOpenTime ? 1 : 0,
    new_order_sound: state.newOrderSound,
  };
}

export default {
  name: "KdsManagement",
  components: {
    OrderChit,
    ModalServerError,
  },
  props: {
    showLoader: {
      type: Function,
      required: true,
    },
    hideLoader: {
      type: Function,
      required: true,
    },
    showNotification: {
      type: Function,
      required: true,
    },
    getPhysicalBranch: {
      type: Function,
      default: () => {},
    },
    initialPrinterId: {
      type: Number,
      default: null,
    },
    initialStatus: {
      type: String,
      default: null,
    },
    // The page level passcode gate, owned by StockByPhysicalBranch. The board
    // stays readable without it; only the writes go through it.
    requirePasscode: {
      type: Function,
      required: true,
    },
    handlePasscodeRequired: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      ORDER_STATUS,
      NAME_FIELD_OPTIONS,
      DESCRIPTION_FIELD_OPTIONS,
      NEW_ORDER_SOUND,
      NEW_ORDER_SOUND_OPTIONS,
      merchantId: 0,
      physicalBranchId: 0,
      tab: ORDER_STATUS.PREPARING,
      printers: [],
      selectedPrinterId: null, // null = "Expeditor / All"
      selectedTransactionId: null,
      orders: [],
      pollInterval: null,
      // Random first delay before pollInterval starts (see armPollTimer).
      pollPhaseTimer: null,
      pollIntervalMs: null,
      tickInterval: null,
      realtime: null,
      realtimeConnected: false,
      refreshTimer: null,
      // When the armed refreshTimer fires, so a sooner request can replace it.
      refreshDueAt: 0,
      // A signal landed while a read was in flight and still owes us one more.
      refreshPending: false,
      isDestroyed: false,
      isFetchingList: false,
      isUserFetching: false,
      alertAfterMin: DEFAULT_ALERT_AFTER_MIN,
      sortOrder: "DESC",
      now: Date.now(),
      settings: defaultSettings(),
      draft: defaultSettings(),
      // False until the first read brings the branch's settings, so the sound
      // chip does not flash the default before the real value.
      settingsLoaded: false,
      serverErrorVisible: false,
      serverErrorMessage: "",
      audioContext: null,
      alertLoopTimer: null,
      // M1024: transaction ids already seen on this station's Preparing board.
      // null until the first read seeds it, so opening the board stays quiet.
      seenOrderIds: null,
    };
  },
  computed: {
    isSoundOff() {
      return this.settings.newOrderSound === NEW_ORDER_SOUND.OFF;
    },
    newOrderSoundHint() {
      return NEW_ORDER_SOUND_OPTIONS.find((o) => o.value === this.draft.newOrderSound)?.hint ?? "";
    },
    soundLabel() {
      if (this.settings.newOrderSound === NEW_ORDER_SOUND.ONCE) return "Sound: Once";
      return this.isSoundOff ? "Sound Off" : "Sound: Repeat";
    },
    isDirectLink() {
      return this.initialPrinterId !== null && this.initialStatus !== null;
    },
    storageKey() {
      return `kds.selectedPrinterId.${this.physicalBranchId}`;
    },
    alertAfterMs() {
      return this.alertAfterMin * 60 * 1000;
    },
    sortLabel() {
      return this.sortOrder === "DESC" ? this.t.newest : this.t.oldest;
    },
    t() {
      return PAGE_LABELS;
    },
    visiblePrinters() {
      return this.printers.filter((p) => p.id === this.selectedPrinterId);
    },
    visibleChits() {
      // No station selected (e.g., the branch has no printers) → show nothing.
      if (this.selectedPrinterId === null) return [];
      const stationId = Number(this.selectedPrinterId);
      // Done is per station: the Ready tab list also carries orders other
      // stations are still cooking, and vice versa, so the tab is decided
      // here for the station on screen.
      const wantReady = this.tab === ORDER_STATUS.READY;
      return this.orders
        .filter((order) => isStationReady(order, stationId) === wantReady)
        .map((order) => {
          const items = (order.items ?? []).filter((item) =>
            resolvePrinterIds(item).includes(stationId)
          );
          return {
            order,
            items,
            struckKeys: order.struck_item_keys?.[stationId] ?? [],
          };
        })
        .filter((chit) => chit.items.length > 0);
    },
  },
  watch: {
    selectedPrinterId(value) {
      if (this.isDirectLink) return;
      if (value === null || value === undefined) {
        localStorage.removeItem(this.storageKey);
      } else {
        localStorage.setItem(this.storageKey, String(value));
      }
    },
    alertAfterMin() {
      this.now = Date.now();
    },
    "settings.alertMinutes"(value) {
      // Keep the legacy topbar Alert-after select in sync with the merchant
      // setting — they drive the same elapsed-time threshold.
      this.alertAfterMin = value;
    },
    // M1071: the loop is for orders nobody has picked up, so it ends once
    // Preparing is empty. A tab switch or refresh also clears the list while
    // its read is out; that is not an empty board, so it is skipped.
    "visibleChits.length"(count) {
      if (count === 0 && this.tab === ORDER_STATUS.PREPARING && !this.isFetchingList) {
        this.stopAlertLoop();
      }
    },
    // M1072: only Repeat loops; Once and Off end a loop already running.
    "settings.newOrderSound"(value) {
      if (value !== NEW_ORDER_SOUND.REPEAT) this.stopAlertLoop();
    },
    // The BE reads the saved setting itself, so the board only has to go
    // back for a fresh list once the value it was told about changes —
    // otherwise the new filter would not show until the next poll.
    "settings.displayUnpaidBills"() {
      // The bills this lets in are not new orders.
      this.seenOrderIds = null;
      this.orders = [];
      this.fetchOrders(true);
    },
  },
  created() {
    this.physicalBranchId = this.$route.params.branch_id ?? 0;
    this.merchantId = this.$route.params.merchant_id ?? 1;
  },
  async mounted() {
    if (this.isDirectLink) {
      this.tab =
        this.initialStatus === "ready"
          ? ORDER_STATUS.READY
          : ORDER_STATUS.PREPARING;
    }

    await this.loadPrinters();

    // Direct-link: validate the requested printer exists for this branch.
    // Unknown printer id → 404 rather than silently dropping to a default.
    if (this.isDirectLink) {
      const exists = this.printers.some(
        (p) => p.id === this.initialPrinterId
      );
      if (!exists) {
        return;
      }
      this.selectedPrinterId = this.initialPrinterId;
    }

    // Browsers keep audio locked until the page gets a click. Added whatever
    // the setting, which only arrives with the first read below.
    document.addEventListener("click", this.unlockAudio);

    await this.fetchOrders();

    this.armPollTimer();
    this.tickInterval = setInterval(() => {
      this.now = Date.now();
    }, TICK_INTERVAL_MS);

    // Last, and not awaited: the board must paint before any socket work, and
    // connection.ready triggers a read of its own once it lands.
    this.startRealtime();
  },
  unmounted() {
    // First, because the teardown below calls back into this component.
    this.isDestroyed = true;

    clearTimeout(this.pollPhaseTimer);
    this.pollPhaseTimer = null;
    clearInterval(this.pollInterval);
    this.pollInterval = null;
    clearInterval(this.tickInterval);
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;

    if (this.realtime) {
      this.realtime.stop();
      this.realtime = null;
    }
    this.stopAlertLoop();
    document.removeEventListener("click", this.unlockAudio);
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
    // If the modal happened to be open when the user navigated away, dispose
    // so Bootstrap removes the backdrop + restores body scroll.
    const el = this.$refs.settingsModal;
    if (el) Modal.getInstance(el)?.dispose();
  },
  methods: {
    /**
     * Arm the fallback timer at the rate the socket's state calls for.
     *
     * Never null, at any rate. The KDS endpoint runs behind stock.passcode in
     * refresh mode, so this request is also what slides the passcode's hour
     * forward — a board that stopped calling it would lock mid shift, which is
     * a far worse failure than a stale chit.
     */
    armPollTimer() {
      if (this.isDestroyed) return;

      const wanted = this.realtimeConnected
        ? HEARTBEAT_INTERVAL_MS
        : POLL_INTERVAL_MS;

      if (
        (this.pollInterval || this.pollPhaseTimer) &&
        this.pollIntervalMs === wanted
      ) {
        return;
      }

      clearTimeout(this.pollPhaseTimer);
      clearInterval(this.pollInterval);
      this.pollInterval = null;
      this.pollIntervalMs = wanted;

      // A socket restart re-arms every board in the same second. Starting the
      // interval at a random point in its period keeps them from polling in
      // lockstep for the rest of the shift.
      this.pollPhaseTimer = setTimeout(() => {
        this.pollPhaseTimer = null;
        this.pollInterval = setInterval(() => {
          // trailing: false — a tick names no particular change, so a read
          // already running is exactly what this tick would have asked for.
          this.requestBoardRefresh(0, { trailing: false });
        }, wanted);
      }, Math.random() * wanted);
    },

    /**
     * Every request to re-read the board goes through here — timer tick, socket
     * signal, reconnect resync — rather than calling fetchOrders directly.
     *
     * A burst of signals costs one read, and a signal arriving mid-read is never
     * silently dropped. One POS push moves several orders at once, so the burst
     * is the normal case rather than the exception.
     */
    requestBoardRefresh(delayMs = COALESCE_MS, { trailing = true } = {}) {
      if (this.isDestroyed) return;

      if (this.isFetchingList) {
        if (trailing) this.refreshPending = true;
        return;
      }

      // An armed timer covers this request unless it fires later than asked —
      // a real change must not wait behind a reconnect's random resync delay.
      if (this.refreshTimer) {
        if (Date.now() + delayMs >= this.refreshDueAt) return;
        clearTimeout(this.refreshTimer);
      }

      this.refreshDueAt = Date.now() + delayMs;
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        // fetchOrders spends the trailing read itself if a signal lands mid-read.
        this.fetchOrders();
      }, delayMs);
    },

    // Realtime is best effort. If the token call fails or the socket never
    // connects, the board simply keeps polling at the old ten-second rate and
    // nothing above this needs to know.
    async startRealtime() {
      if (this.isDestroyed || this.realtime || !this.physicalBranchId) return;

      this.realtime = new BoardRealtime({
        fetchToken: async () => {
          const response = await SettingStoreService.getWsTokenForBoard({
            merchant_id: this.merchantId,
            physical_branch_id: this.physicalBranchId,
          });

          const result = response.data.result;

          return result && result.isSuccess ? response.data.data : null;
        },
        onSignal: (signal) => {
          // A resync is every board in every outlet re-reading at once, so it
          // gets spread out. A real board.updated is one kitchen's own orders
          // moving and stays prompt.
          const delay =
            signal && signal.resync
              ? Math.random() * RESYNC_JITTER_MS
              : COALESCE_MS;

          this.requestBoardRefresh(delay);
        },
        onConnectionChange: (connected) => {
          this.realtimeConnected = connected;
          this.armPollTimer();
        },
      });

      // Read back rather than kept from `new`: data() hands out a reactive
      // proxy, and the identity check below has to compare like with like.
      const realtime = this.realtime;

      // A false start leaves no socket behind, but a non-null this.realtime
      // would still block every later attempt, so clear it.
      if (!(await realtime.start()) && this.realtime === realtime) {
        realtime.stop();
        this.realtime = null;
      }
    },

    // Server-side failures from API calls surface in a shared modal instead
    // of the toast so the user has to acknowledge them before continuing.
    showServerError(message) {
      this.serverErrorMessage = message;
      this.serverErrorVisible = true;
    },

    dateFormat(date) {
      return date == null ? null : moment(date).format("YYYY-MM-DD");
    },

    changeTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.seenOrderIds = null;
      this.orders = [];
      this.selectedTransactionId = null;
      this.fetchOrders(true);
    },

    toggleSort() {
      this.sortOrder = this.sortOrder === "DESC" ? "ASC" : "DESC";
      this.orders = [];
      this.fetchOrders(true);
    },

    refresh() {
      this.orders = [];
      this.fetchOrders(true);
    },

    // Placeholders — UI is wired up so the topbar matches the new design,
    // but the actions themselves are deferred until product confirms scope.
    onLanguage() {
      // TODO: integrate vue-i18n locale switching here.
    },

    onPrint() {
      // TODO: confirm print scope — all visible chits vs the selected one.
    },

    openSettings() {
      // Working copy — only committed to `settings` when SAVE is clicked.
      this.draft = JSON.parse(JSON.stringify(this.settings));
      const el = this.$refs.settingsModal;
      if (el) Modal.getOrCreateInstance(el).show();
    },

    closeSettingsModal() {
      const el = this.$refs.settingsModal;
      if (el) Modal.getOrCreateInstance(el).hide();
    },

    saveSettings() {
      const next = JSON.parse(JSON.stringify(this.draft));

      this.requirePasscode(() => this.sendSettings(next));
    },

    async sendSettings(next) {
      try {
        const response = await SettingStoreService.saveKdsSettings({
          merchant_id: this.merchantId,
          physical_branch_id: this.physicalBranchId,
          ...settingsToApi(next),
        });
        if (!response.data.result.isSuccess) {
          if (!this.handlePasscodeRequired(response, () => this.sendSettings(next))) {
            this.showServerError(response.data.result.message);
          }
          return;
        }
      } catch (error) {
        this.showServerError(error);
        return;
      }
      this.settings = next;
      this.alertAfterMin = this.settings.alertMinutes;
      this.closeSettingsModal();
    },

    onSelect(order) {
      // Single-select toggle: clicking the active card deselects it.
      this.selectedTransactionId =
        this.selectedTransactionId === order.transaction_id
          ? null
          : order.transaction_id;
    },

    async loadPrinters() {
      try {
        const response = await MenusetService.getPrinters({
          merchant_id: this.merchantId,
          physical_branch_id: this.physicalBranchId,
        });
        if (response.data.result.isSuccess) {
          this.printers = response.data.data ?? [];
          // Direct-link mode picks its station from the URL — skip the
          // localStorage restore so we don't briefly flicker to the
          // operator's last-used station before snapping to the URL one.
          if (!this.isDirectLink) {
            this.restoreSelectedPrinter();
            this.ensureSelectedPrinterValid();
          }
        } else {
          this.showServerError(response.data.result.message);
        }
      } catch (error) {
        // The printer list sits behind the merchant login and the kitchen
        // display only runs on a signed-in device, so a 401 here means the
        // session is gone - not that anything is wrong with the branch.
        if (error?.response?.status === 401) {
          TokenService.removeUser();
          this.$router.push("/sign-in");
          return;
        }
        this.showServerError(error);
      }
    },

    restoreSelectedPrinter() {
      const stored = localStorage.getItem(this.storageKey);
      const storedId = stored !== null ? Number(stored) : null;
      const exists =
        storedId !== null && this.printers.some((p) => p.id === storedId);
      // ensureSelectedPrinterValid falls back to the first printer.
      this.selectedPrinterId = exists ? storedId : null;
    },

    ensureSelectedPrinterValid() {
      if (this.selectedPrinterId !== null) return;
      const first = this.printers[0];
      this.selectedPrinterId = first ? first.id : null;
    },

    async fetchOrders(showSpinner = false) {
      if (!this.physicalBranchId) return;

      // A read is already out, possibly for the tab or sort this call just
      // replaced. Owe one more instead of dropping the call.
      if (this.isFetchingList) {
        this.refreshPending = true;
        return;
      }

      this.isFetchingList = true;
      // This read covers every signal that arrived before it started.
      this.refreshPending = false;
      if (showSpinner) this.isUserFetching = true;
      // What this read asks for. A tab switch or sort toggle mid-read only
      // queues a re-read, so the answer that lands here may be for a board the
      // user has already left.
      const tab = this.tab;
      const sortOrder = this.sortOrder;
      let isStale = false;
      try {
        const response = await SettingStoreService.getOrderKDSByPhysicBranch({
          merchant_id: this.merchantId,
          physical_branch_id: this.physicalBranchId,
          search_keyword: "",
          from_date: this.dateFormat(new Date()),
          to_date: this.dateFormat(new Date()),
          type: tab,
          sort_order: sortOrder,
        });
        isStale = tab !== this.tab || sortOrder !== this.sortOrder;
        if (isStale) {
          // Keep the board empty rather than paint the old tab's chits; the
          // re-read below brings the right ones.
          this.refreshPending = true;
        } else if (response.data.result.isSuccess) {
          this.orders = response.data.data.orders ?? [];
          if (response.data.data.physical_branch) {
            this.getPhysicalBranch(response.data.data.physical_branch);
          }
          if (response.data.data.kds_settings) {
            this.settings = settingsFromApi(response.data.data.kds_settings);
            this.alertAfterMin = this.settings.alertMinutes;
          }
          this.settingsLoaded = true;
          // M1072: after the settings, so a sound changed on another screen
          // already applies to the order this read brought in.
          this.ringIfNewOrders();
        } else {
          this.showServerError(response.data.result.message);
        }
      } catch (error) {
        isStale = tab !== this.tab || sortOrder !== this.sortOrder;
        if (isStale) {
          this.refreshPending = true;
        } else {
          this.showServerError(error);
        }
      } finally {
        this.isFetchingList = false;
        this.isUserFetching = false;
        if (showSpinner) this.hideLoader();
      }

      // A signal landed mid-read — whoever started the read: the refresh
      // button, a tab switch or a socket signal. The read above cannot be
      // trusted to include it, so spend exactly one more. Straight away when
      // the board is sitting empty on a tab it has no rows for yet.
      if (this.refreshPending && !this.isDestroyed) {
        this.refreshPending = false;
        this.requestBoardRefresh(isStale ? 0 : COALESCE_MS);
      }
    },

    // M1024: three beeps when a chit this board has not shown before lands on
    // Preparing - socket signal or poll alike, since both come through here.
    // How it sounds is the branch's New order sound (M1072); Repeat loops until
    // Preparing is empty (M1071). Ids are kept, not replaced, so a Recalled
    // order does not ring again.
    ringIfNewOrders() {
      if (this.tab !== ORDER_STATUS.PREPARING) return;
      const ids = this.visibleChits.map((chit) => chit.order.transaction_id);
      // The watcher misses a board that was already cleared for this read.
      if (ids.length === 0) this.stopAlertLoop();

      if (this.seenOrderIds === null) {
        this.seenOrderIds = new Set(ids);
        return;
      }

      const hasNew = ids.some((id) => !this.seenOrderIds.has(id));
      ids.forEach((id) => this.seenOrderIds.add(id));
      if (!hasNew) return;

      // M1061: Off stays off until Settings turns it back on. Orders that land
      // meanwhile are already counted as seen, so turning it on does not ring
      // for them.
      if (this.isSoundOff) return;
      if (this.settings.newOrderSound === NEW_ORDER_SOUND.ONCE) {
        this.playAlert();
        return;
      }
      // Ring now and restart the beat, so the loop's next beep does not overlap.
      this.startAlertLoop();
    },

    startAlertLoop() {
      this.stopAlertLoop();
      if (this.isDestroyed) return;
      this.playAlert();
      this.alertLoopTimer = setInterval(() => this.playAlert(), ALERT_REPEAT_MS);
    },

    stopAlertLoop() {
      clearInterval(this.alertLoopTimer);
      this.alertLoopTimer = null;
    },

    getAudioContext() {
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return null;
        this.audioContext = new AudioContextClass();
      }
      return this.audioContext;
    },

    unlockAudio() {
      document.removeEventListener("click", this.unlockAudio);
      try {
        const context = this.getAudioContext();
        if (context && context.state === "suspended") {
          context.resume().catch(() => {});
        }
      } catch (e) {
        // No Web Audio in this browser: the alert stays silent.
      }
    },

    // Three short beeps.
    playAlert() {
      if (this.isSoundOff || this.isDestroyed) return;
      try {
        const context = this.getAudioContext();
        if (!context) return;
        if (context.state === "suspended") context.resume().catch(() => {});

        const start = context.currentTime + 0.05;
        for (let i = 0; i < 3; i++) {
          const at = start + i * 0.3;
          const oscillator = context.createOscillator();
          const gain = context.createGain();
          oscillator.type = "sine";
          oscillator.frequency.setValueAtTime(880, at);
          gain.gain.setValueAtTime(0.0001, at);
          gain.gain.exponentialRampToValueAtTime(0.4, at + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.2);
          oscillator.connect(gain);
          gain.connect(context.destination);
          oscillator.start(at);
          oscillator.stop(at + 0.22);
        }
      } catch (e) {
        // Audio is a convenience; never let it break the board.
      }
    },

    onDone(order) {
      this.moveStation(order, "Move successfully");
    },

    // M985: a tap on a row's QTY strikes it on this station (tap again to
    // undo). Display only — the chit stays on Preparing until DONE.
    onStrike(order, lineKey) {
      this.requirePasscode(() => this.sendStrike(order, lineKey));
    },

    sendStrike(order, lineKey) {
      const stationId = Number(this.selectedPrinterId);
      const target = this.orders.find(
        (o) => o.transaction_id === order.transaction_id
      );
      if (!target || this.selectedPrinterId === null) return;

      // Optimistic, per key: rapid taps on several rows each flip only their
      // own key, so an earlier response can't wipe a later tap.
      const flip = () => {
        const all = { ...(target.struck_item_keys ?? {}) };
        const keys = all[stationId] ?? [];
        all[stationId] = keys.includes(lineKey)
          ? keys.filter((k) => k !== lineKey)
          : [...keys, lineKey];
        target.struck_item_keys = all;
      };
      flip();

      SettingStoreService.strikeKdsItem({
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId,
        transaction_id: order.transaction_id,
        printer_id: stationId,
        line_key: lineKey,
      }).then(
        (response) => {
          if (!response.data.result.isSuccess) {
            flip();

            if (
              !this.handlePasscodeRequired(response, () =>
                this.sendStrike(order, lineKey)
              )
            ) {
              this.showServerError(response.data.result.message);
            }
          }
        },
        (error) => {
          flip();
          this.showServerError(error);
        }
      );
    },

    onRecall(order) {
      this.moveStation(order, "Recalled to preparing");
    },

    // Collect transitions the order from READY to SUCCESS on the BE. The
    // chit disappears from the READY tab; BE fires the order_collect WA
    // template when the physical branch has kds_send_wa_on_collect enabled
    // AND the merchant has sufficient WA balance.
    onCollect(order) {
      // Ask for the passcode before the chit is dropped, otherwise cancelling
      // the dialog would leave the board missing an order.
      this.requirePasscode(() => this.sendCollect(order));
    },

    sendCollect(order) {
      const index = this.orders.findIndex(
        (o) => o.transaction_id === order.transaction_id
      );
      if (index === -1) return;
      const [removed] = this.orders.splice(index, 1);
      if (this.selectedTransactionId === order.transaction_id) {
        this.selectedTransactionId = null;
      }

      SettingStoreService.collectOrderKDS({
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId,
        transaction_id: order.transaction_id,
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.showNotification("Order collected", "success");
          } else {
            this.orders.splice(index, 0, removed);

            if (
              !this.handlePasscodeRequired(response, () =>
                this.sendCollect(order)
              )
            ) {
              this.showServerError(response.data.result.message);
            }
          }
        },
        (error) => {
          this.orders.splice(index, 0, removed);
          this.showServerError(error);
        }
      );
    },

    // DONE/Recall move only the station on screen — the other stations keep
    // their chit until they press Done themselves. The order's own
    // kitchen_status turns Ready once the last station is done (BE decides).
    // Optimistically patch the order so the chit leaves this tab at once,
    // and put it back if order/move-kds fails.
    moveStation(order, successMessage) {
      this.requirePasscode(() => this.sendMoveStation(order, successMessage));
    },

    sendMoveStation(order, successMessage) {
      const stationId = Number(this.selectedPrinterId);
      const target = this.orders.find(
        (o) => o.transaction_id === order.transaction_id
      );
      if (!target || this.selectedPrinterId === null) return;

      const before = {
        kitchen_status: target.kitchen_status,
        ready_printer_ids: target.ready_printer_ids,
        struck_item_keys: target.struck_item_keys,
      };
      // Recall clears this station's struck rows (the BE does the same, M985),
      // so the chit comes back clean.
      const isRecall = isStationReady(target, stationId);
      Object.assign(
        target,
        toggleStationReady(target, stationId),
        isRecall
          ? { struck_item_keys: { ...(target.struck_item_keys ?? {}), [stationId]: [] } }
          : {}
      );
      if (this.selectedTransactionId === order.transaction_id) {
        this.selectedTransactionId = null;
      }

      SettingStoreService.moveOrderKDS({
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId,
        transaction_id: order.transaction_id,
        printer_id: stationId,
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.showNotification(successMessage, "success");
            // Pick up the BE's word on kitchen_status (the last station's
            // Done is what turns the order Ready for Collect).
            this.requestBoardRefresh();
          } else {
            Object.assign(target, before);

            if (
              !this.handlePasscodeRequired(response, () =>
                this.sendMoveStation(order, successMessage)
              )
            ) {
              this.showServerError(response.data.result.message);
            }
          }
        },
        (error) => {
          Object.assign(target, before);
          this.showServerError(error);
        }
      );
    },
  },
};
</script>

<style scoped>
.kds {
  color: #191c1e;
  padding: 0 1.5rem 1.5rem;
  background: #f1f5f9;
  min-height: 100%;
}

/* ---------- Top bar (dark navy header per design) ---------- */
.kds__topbar {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  flex-wrap: wrap; /* let stations + controls fall onto their own rows once
                       they can't both fit — no fixed breakpoint to guess */
  row-gap: 12px;
  min-height: 80px;
  padding: 12px 24px;
  background: #131b2e; /* design: bg-primary-container */
  color: #7c839b;      /* design: text-on-primary-container */
  margin: 16px 0 0;
  border-radius: 8px;  /* soften the inset module on the slate page */
}

/* Topbar tabs now host the scrollable station pills — take the leftover
   space and let the inner scroll area shrink so the right-side controls
   are never pushed off screen. min-width forces this cluster onto its own
   row (via the wrap above) once the container is too narrow to fit both
   this and .kds__controls comfortably side by side. */
.kds__tabs {
  display: flex;
  align-items: center;
  flex: 1 1 260px;
  min-width: 260px;
}

/* Preparing / Ready pills below the topbar — large rounded pills, solid
   blue when active, outlined white when idle. */
.kds__tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 32px;
  border-radius: 9999px;
  background: #fff;
  border: 1px solid #c6c6cd;
  color: #45464d;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease,
              border-color 0.15s ease, transform 0.1s ease;
}

.kds__tab:hover {
  background: #e6e8ea;
}

.kds__tab:active {
  transform: scale(0.96);
}

.kds__tab--active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.kds__tab--active:hover {
  background: #3b82f6;
}

/* Disabled tabs: the HTML `disabled` attribute is enough to swallow the
   click — we deliberately don't apply any visual style here so the tab
   looks identical whether it's mid-fetch or idle. Avoids the brief
   "flicker" UX the user reported when the disable state turned on then
   off within a few hundred ms. */

/* ---------- Right cluster (Alert / Sort / Lang / Print / Refresh / Settings) ---------- */
.kds__controls {
  display: flex;
  align-items: center;
  gap: 16px; /* gap-4 in the design */
  flex-shrink: 0; /* never compress the controls — keeps each chip stable */
  overflow-x: auto; /* scroll instead of being clipped by .kds__topbar's overflow:hidden when space is tight */
  overflow-y: hidden;
  max-width: 100%;
}

/* Base "chip" style — light gray pill on the dark bar. */
.kds__control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
  background: #e6e8ea;       /* design: bg-surface-container-high */
  border: 1px solid #c6c6cd; /* design: border-outline-variant */
  border-radius: 8px;
  color: #191c1e;            /* design: text-on-surface */
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0; /* chips keep their natural width — no squeezing */
  transition: background 0.2s ease;
}

.kds__control:hover {
  background: #e0e3e5; /* design: hover:bg-surface-variant */
}

/* Disabled chips (Sort + Refresh while a fetch is in flight):
   same rationale as the tabs above — block the click but keep the chip
   visually identical so no "flicker" during the short fetch window. */

/* Refresh icon spins while the fetch is running — intentional loading
   feedback (click was accepted), not a "disabled" style cue. Kept even
   though disabled style was dropped. */
.kds__icon--spinning {
  animation: kds-spin 1s linear infinite;
}

.kds__icon-btn {
  width: 48px;
  padding: 0;
  justify-content: center;
}

.kds__control-icon {
  font-size: 20px;
}

.kds__alert {
  padding: 0 12px;
}

.kds__alert-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
}

.kds__alert-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  opacity: 0.6;
}

.kds__alert-select {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  color: #191c1e;
  cursor: pointer;
}

.kds__alert-select:focus {
  outline: none;
  box-shadow: none;
}

.kds__sort {
  min-width: 116px;
  justify-content: flex-start;
}

/* Keeps its place in the toolbar while the settings load. */
.kds__audio--pending {
  visibility: hidden;
}

/* Sound Off: white chip, muted text - same cue as Order Management's button. */
.kds__audio--off,
.kds__audio--off:hover {
  background: #ffffff;
  color: #495057;
  border-color: #adb5bd;
}

.kds__sort-label {
  text-transform: uppercase;
  font-size: 14px;
  min-width: 60px;
  text-align: left;
}

.kds__lang {
  font-weight: 700;
  font-size: 14px;
}

/* Vertical divider between EN and Print, matching the design's
   `w-px h-8 bg-outline-variant mx-2` */
.kds__divider {
  width: 1px;
  height: 32px;
  background: #c6c6cd;
  margin: 0 -8px; /* compensate for gap-16 on both sides → visual gap-8 */
  align-self: center;
}

/* ---------- Tab row below the topbar (Preparing / Ready pills) ---------- */
.kds__stations {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  margin-bottom: 16px;
}

/* Scroll container — wraps the station pills inside the dark topbar. */
.kds__stations-scroll {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  /* Reserve a stable gutter so the bar's vertical alignment doesn't jump
     when scrollable / non-scrollable. */
  padding-bottom: 6px;
  scrollbar-gutter: stable;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #c6c6cd transparent;
}

/* WebKit / Chromium / Edge */
.kds__stations-scroll::-webkit-scrollbar {
  height: 6px;
}

.kds__stations-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.kds__stations-scroll::-webkit-scrollbar-thumb {
  background: #c6c6cd;
  border-radius: 9999px;
  transition: background 0.15s ease;
}

.kds__stations-scroll::-webkit-scrollbar-thumb:hover {
  background: #76777d;
}

.kds__stations-scroll::-webkit-scrollbar-thumb:active {
  background: #3b82f6;
}

.kds__station {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px;
  border-radius: 9999px;
  background: #fff;
  border: 1px solid #c6c6cd;
  color: #45464d;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.kds__station--active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.kds__station-icon {
  font-size: 18px;
}

/* ---------- Grid ---------- */
.kds__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px 16px;
  row-gap: 32px;
  padding-bottom: 16px;
}

@media (max-width: 1280px) {
  .kds__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .kds__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .kds__grid {
    grid-template-columns: 1fr;
  }
}

.kds__empty {
  text-align: center;
  color: #76777d;
  padding: 40px 0;
  font-size: 16px;
  font-weight: 600;
  min-height: calc(100vh - 260px); /* topbar + stations + parent header */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.kds__spinner {
  font-size: 40px;
  color: #3b82f6;
  animation: kds-spin 1s linear infinite;
}

@keyframes kds-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* ---------- Settings modal ---------- */
.kds-settings__header {
  border-bottom: 1px solid #e6e8ea;
}

.kds-settings__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
}

.kds-settings__row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kds-settings__row--inline {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.kds-settings__label {
  font-weight: 600;
  color: #45464d;
  font-size: 14px;
  margin: 0;
}

.kds-settings__inline-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.kds-settings__name-select {
  width: 140px;
  height: 36px;
  border: 1px solid #c6c6cd;
  border-radius: 6px;
  padding: 0 8px;
  font-weight: 600;
  font-size: 14px;
  color: #191c1e;
  background: #fff;
  cursor: pointer;
}
.kds-settings__name-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.kds-settings__name-select option:disabled {
  color: #a5a6ac;
}

.kds-settings__alert-select {
  width: 80px;
  height: 36px;
  border: 1px solid #c6c6cd;
  border-radius: 6px;
  padding: 0 8px;
  font-weight: 600;
  font-size: 14px;
  color: #191c1e;
  background: #fff;
  cursor: pointer;
}

.kds-settings__alert-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.kds-settings__unit {
  color: #76777d;
  font-size: 14px;
}

.kds-settings__unit--divider {
  border-left: 1px solid #e0e3e5;
  padding-left: 12px;
  margin-left: 4px;
  font-weight: 600;
  color: #45464d;
}

.kds-settings__color {
  width: 36px;
  height: 36px;
  border: 1px solid #c6c6cd;
  border-radius: 6px;
  padding: 2px;
  cursor: pointer;
  background: #fff;
}

/* Plain CSS toggle — replaces MaterialToggleButton for the KDS modal so we
   own the controlled-component behavior (v-model directly on draft.* keeps
   ON/OFF reliable without the legacy component's defaultState watcher). */
.kds-toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  cursor: pointer;
  flex: 0 0 auto;
}

.kds-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.kds-toggle__track {
  position: absolute;
  inset: 0;
  background: #fff;
  border: 1px solid #b5b6b8;
  border-radius: 9999px;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.kds-toggle__thumb {
  position: absolute;
  top: 3px;
  left: 4px;
  width: 18px;
  height: 18px;
  background: #b5b6b8;
  border-radius: 50%;
  box-shadow: 0 0 1px #666;
  transition: transform 0.2s ease, background 0.2s ease;
}

.kds-toggle--on .kds-toggle__track {
  background: #1871bd;
  border-color: #1871bd;
}

.kds-toggle--on .kds-toggle__thumb {
  background: #fff;
  transform: translateX(22px);
}

.kds-settings__hint {
  margin-top: 2px;
  color: #76777d;
  font-size: 12px;
}

/* M1072: three-way switch, same blue as the on-toggle. */
.kds-segmented {
  display: inline-flex;
  flex: 0 0 auto;
  border: 1px solid #c6c6cd;
  border-radius: 6px;
  overflow: hidden;
}

.kds-segmented__option {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 34px;
  padding: 0 12px;
  border: 0;
  border-left: 1px solid #c6c6cd;
  background: #fff;
  color: #45464d;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.kds-segmented__option:first-child {
  border-left: 0;
}

.kds-segmented__option--active {
  background: #1871bd;
  color: #fff;
}

.kds-segmented__icon {
  font-size: 18px;
}

.kds-settings__radio-group {
  display: flex;
  gap: 16px;
}

.kds-settings__radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  cursor: pointer;
}

/* ---------- Responsive: tablet ---------- */
@media (max-width: 1024px) {
  .kds__topbar {
    padding: 0 16px;
  }

  .kds__controls {
    gap: 10px;
  }

  .kds__control {
    padding: 0 12px;
  }
}

/* ---------- Responsive: mobile / narrow tablet ---------- */
@media (max-width: 768px) {
  .kds {
    padding: 0 1rem 1rem;
  }

  .kds__topbar {
    padding: 10px 12px;
  }

  .kds__controls {
    padding-bottom: 4px;
  }

  .kds__stations {
    flex-wrap: wrap;
    gap: 8px;
  }

  .kds__tab {
    padding: 10px 20px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .kds__control {
    height: 42px;
    padding: 0 12px;
    font-size: 13px;
  }

  .kds__icon-btn {
    width: 42px;
  }

  .kds__sort {
    min-width: 96px;
  }

  .kds-settings__body {
    padding: 16px;
  }

  .kds-settings__row--inline {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .kds-settings__inline-controls {
    flex-wrap: wrap;
    row-gap: 8px;
  }
}
</style>
